/**
 * Save-slot picker — v0M15.2.
 *
 * A modal overlay listing the 5 named save slots + the autosave slot,
 * shared between two call sites:
 *
 *  - **Save mode** (in-game menu → "Save Game"): each empty slot exposes
 *    a name input + Save button; each occupied slot exposes Overwrite +
 *    Delete. The autosave slot is read-only (autosave is written by the
 *    autosave loop at v0M15.5, not through the picker).
 *
 *  - **Load mode** (Main Menu → "Load Game" and post-victory → "Load
 *    Previous Save"): each occupied slot exposes Load + Delete; empty
 *    slots render disabled with "(Empty)" text.
 *
 * The picker never touches sim state directly. It hands slot IDs back to
 * the caller through callbacks; the caller performs the actual save /
 * load and closes the picker via the returned `destroy()`.
 *
 * Styling: uses the shared parchment palette in menu-styles.ts —
 * `.su-save-picker-*` classes. Injected on mount via `injectMenuStyles`
 * so the picker works whether or not `mountMenus` is currently mounted
 * (needed for in-game usage where the menu overlay is hidden).
 */

import { injectMenuStyles } from './menu-styles';
import { listSlots, ALL_SLOT_IDS } from '../save-storage';
import type { SlotEntry, SlotId, SlotMeta } from '../save-storage';

/** Which mode the picker was mounted in — controls action buttons per row. */
export type SaveSlotPickerMode = 'save' | 'load';

export interface SaveSlotPickerCallbacks {
  /** Save mode only. Called when user commits a save (empty slot Save, or occupied slot Overwrite). */
  onSaveToSlot?(id: SlotId, name: string): void;
  /** Load mode only. Called when user clicks Load on an occupied slot. */
  onLoadFromSlot?(id: SlotId): void;
  /** Both modes. Called when user clicks Delete on an occupied slot AFTER the confirm dialog is accepted. */
  onDeleteSlot?(id: SlotId): void;
  /** Called when user clicks Cancel or presses Escape. The picker does not tear itself down — call `destroy()`. */
  onCancel(): void;
}

export interface SaveSlotPickerHandle {
  /** Tear down the overlay + remove the Escape handler. Idempotent. */
  destroy(): void;
  /** Re-read localStorage and re-render (e.g. after the caller writes / deletes a slot). */
  refresh(): void;
}

/**
 * Mount the picker into `root`. The overlay uses a high z-index so it
 * sits above both the map and any in-game panels. Caller is responsible
 * for pausing the sim (or not) — the picker is UI-only.
 */
export function mountSaveSlotPicker(
  root: HTMLElement,
  mode: SaveSlotPickerMode,
  callbacks: SaveSlotPickerCallbacks,
): SaveSlotPickerHandle {
  injectMenuStyles();

  // Reuse the main menu overlay + panel classes so the parchment palette
  // and layout match the rest of the menu system exactly.
  const overlay = document.createElement('div');
  overlay.className = 'su-menu-overlay';
  // The in-game caller may want the picker to appear above the settlement
  // panel (z-index 700) and battle view (z-index 800). Menu overlay is
  // already 1000, so no extra work needed.
  root.appendChild(overlay);

  const panel = document.createElement('div');
  panel.className = 'su-menu-panel';
  overlay.appendChild(panel);

  // Escape closes the picker via onCancel. Keydown listener attached on
  // window so it fires regardless of focus (input inside picker included
  // — Escape still cancels rather than clearing the field).
  const escHandler = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape') {
      ev.preventDefault();
      callbacks.onCancel();
    }
  };
  window.addEventListener('keydown', escHandler);

  function render(): void {
    panel.innerHTML = '';

    const title = document.createElement('h1');
    title.className = 'su-menu-title';
    title.textContent = mode === 'save' ? 'Save Game' : 'Load Game';
    panel.appendChild(title);

    const subtitle = document.createElement('div');
    subtitle.className = 'su-menu-subtitle';
    subtitle.textContent =
      mode === 'save'
        ? 'Choose a slot to save to. Autosave slot is reserved.'
        : 'Choose a save to load.';
    panel.appendChild(subtitle);

    const list = document.createElement('div');
    list.className = 'su-save-picker-list';
    panel.appendChild(list);

    const entries = listSlots();
    const anyOccupied = entries.some((e) => e.meta !== null);

    if (mode === 'load' && !anyOccupied) {
      const empty = document.createElement('div');
      empty.className = 'su-save-picker-empty-note';
      empty.textContent = 'No saved games yet.';
      list.appendChild(empty);
    } else {
      for (const entry of entries) {
        list.appendChild(renderRow(entry));
      }
    }

    const footer = document.createElement('div');
    footer.className = 'su-save-picker-footer';
    panel.appendChild(footer);

    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'su-save-picker-btn';
    cancelBtn.textContent = 'Cancel';
    cancelBtn.onclick = (): void => callbacks.onCancel();
    footer.appendChild(cancelBtn);
  }

  function renderRow(entry: SlotEntry): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'su-save-picker-row';
    if (entry.id === 'autosave') row.classList.add('su-autosave');

    // Slot label column.
    const label = document.createElement('div');
    label.className = 'su-save-picker-slot-label';
    label.textContent = slotLabel(entry.id);
    row.appendChild(label);

    // Body column.
    const body = document.createElement('div');
    body.className = 'su-save-picker-body';
    row.appendChild(body);

    // Actions column.
    const actions = document.createElement('div');
    actions.className = 'su-save-picker-actions';
    row.appendChild(actions);

    if (entry.meta === null) {
      renderEmptyRowContents(entry.id, body, actions);
    } else {
      renderOccupiedRowContents(entry.id, entry.meta, body, actions);
    }
    return row;
  }

  function renderEmptyRowContents(
    id: SlotId,
    body: HTMLDivElement,
    actions: HTMLDivElement,
  ): void {
    if (mode === 'save' && id !== 'autosave') {
      // Save-into-empty-slot: name input + Save button.
      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'su-save-picker-name-input';
      input.placeholder = defaultSaveName(id);
      input.maxLength = 40;
      body.appendChild(input);

      const saveBtn = document.createElement('button');
      saveBtn.className = 'su-save-picker-btn su-primary';
      saveBtn.textContent = 'Save';
      saveBtn.onclick = (): void => {
        const name = input.value.trim() || defaultSaveName(id);
        callbacks.onSaveToSlot?.(id, name);
      };
      // Enter within the input triggers Save.
      input.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter') {
          ev.preventDefault();
          saveBtn.click();
        }
      });
      actions.appendChild(saveBtn);
    } else {
      // Load-into-empty or Save-into-autosave: read-only "(Empty)".
      const empty = document.createElement('div');
      empty.className = 'su-save-picker-name su-empty';
      empty.textContent = id === 'autosave' ? '(No autosave yet)' : '(Empty)';
      body.appendChild(empty);
    }
  }

  function renderOccupiedRowContents(
    id: SlotId,
    meta: SlotMeta,
    body: HTMLDivElement,
    actions: HTMLDivElement,
  ): void {
    const name = document.createElement('div');
    name.className = 'su-save-picker-name';
    name.textContent = meta.name;
    body.appendChild(name);

    const metaLine = document.createElement('div');
    metaLine.className = 'su-save-picker-meta';
    metaLine.textContent = `${formatSavedAt(meta.savedAt)} — Day ${meta.gameDay} — ${meta.scenarioName}`;
    body.appendChild(metaLine);

    if (mode === 'save') {
      // Autosave slot: no Overwrite (autosave-only), only Delete.
      if (id !== 'autosave') {
        const overwriteBtn = document.createElement('button');
        overwriteBtn.className = 'su-save-picker-btn su-primary';
        overwriteBtn.textContent = 'Overwrite';
        overwriteBtn.onclick = (): void => {
          if (window.confirm(`Overwrite "${meta.name}"?`)) {
            callbacks.onSaveToSlot?.(id, meta.name);
          }
        };
        actions.appendChild(overwriteBtn);
      }
    } else {
      // Load mode.
      const loadBtn = document.createElement('button');
      loadBtn.className = 'su-save-picker-btn su-primary';
      loadBtn.textContent = 'Load';
      loadBtn.onclick = (): void => callbacks.onLoadFromSlot?.(id);
      actions.appendChild(loadBtn);
    }

    const deleteBtn = document.createElement('button');
    deleteBtn.className = 'su-save-picker-btn su-danger';
    deleteBtn.textContent = 'Delete';
    deleteBtn.onclick = (): void => {
      if (window.confirm(`Delete save "${meta.name}"?`)) {
        callbacks.onDeleteSlot?.(id);
      }
    };
    actions.appendChild(deleteBtn);
  }

  render();

  let destroyed = false;
  return {
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      window.removeEventListener('keydown', escHandler);
      if (overlay.parentElement) overlay.parentElement.removeChild(overlay);
    },
    refresh(): void {
      if (destroyed) return;
      render();
    },
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function slotLabel(id: SlotId): string {
  if (id === 'autosave') return 'Autosave';
  // slot1..slot5 → "Slot 1" .. "Slot 5"
  const idx = ALL_SLOT_IDS.indexOf(id) + 1;
  return `Slot ${idx}`;
}

function defaultSaveName(id: SlotId): string {
  if (id === 'autosave') return 'Autosave';
  const idx = ALL_SLOT_IDS.indexOf(id) + 1;
  return `Save ${idx}`;
}

/**
 * Format a save timestamp for the row's meta line. Chose the compact
 * `YYYY-MM-DD HH:MM` local format — short enough to fit alongside the
 * game-day + scenario without wrapping, unambiguous across locales
 * (unlike `M/D/YY`), and useful for sorting-by-eye in the picker.
 */
function formatSavedAt(unixMs: number): string {
  const d = new Date(unixMs);
  const pad = (n: number): string => n.toString().padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}
